import * as React from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface ComboboxOption {
  value: string;
  label: string;
  sublabel?: string;
  searchText?: string;
}

interface ComboboxSelectProps {
  id?: string;
  options: ComboboxOption[];
  value?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  /** Width/position overrides for the dropdown without changing the trigger. */
  contentClassName?: string;
  debounceMs?: number;
  /**
   * Render the dropdown without a portal. Use when the combobox is inside a modal
   * Dialog so the list scrolls with the mouse wheel (the dialog's scroll-lock
   * otherwise blocks wheel events on portaled content).
   */
  disablePortal?: boolean;
  /** Modal shell outside its scrolling body, avoiding clipping while preserving wheel input. */
  portalContainer?: HTMLElement | null;
  onValueChange?: (value: string) => void;
  /** Offers a final command row that passes the normalized search to the caller. */
  onCreateOption?: (label: string) => void;
  createOptionLabel?: string;
  /** Final caller-side guard for matches that are not rendered as options. */
  isCreateOptionAllowed?: (normalizedSearch: string) => boolean;
  /**
   * Domain-specific equivalence check used both to keep an existing match
   * visible and to suppress creation of a duplicate.
   */
  isCreateOptionMatch?: (
    option: ComboboxOption,
    normalizedSearch: string,
  ) => boolean;
}

export function ComboboxSelect({
  id,
  options,
  value = "",
  placeholder = "Select an option...",
  searchPlaceholder = "Search...",
  emptyText = "No results found.",
  loading = false,
  disabled = false,
  className,
  contentClassName,
  debounceMs = 150,
  disablePortal = false,
  portalContainer,
  onValueChange,
  onCreateOption,
  createOptionLabel = "Create",
  isCreateOptionAllowed,
  isCreateOptionMatch,
}: ComboboxSelectProps) {
  const [open, setOpen] = React.useState(false);
  const listboxId = React.useId();
  const [searchValue, setSearchValue] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");

  const selectedOption = React.useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  React.useEffect(() => {
    if (options.length < 100) {
      setDebouncedSearch(searchValue);
      return;
    }

    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, debounceMs);

    return () => {
      window.clearTimeout(timer);
    };
  }, [debounceMs, options.length, searchValue]);

  const filteredOptions = React.useMemo(() => {
    const normalizedQuery = debouncedSearch.trim().replace(/\s+/g, " ");
    const query = normalizedQuery.toLowerCase();
    if (!query) return options;

    return options.filter((option) => {
      const labelMatch = option.label.toLowerCase().includes(query);
      const sublabelMatch = option.sublabel?.toLowerCase().includes(query);
      const searchTextMatch = option.searchText?.toLowerCase().includes(query);
      const equivalentCreateMatch = isCreateOptionMatch?.(
        option,
        normalizedQuery,
      );
      return (
        labelMatch ||
        sublabelMatch ||
        searchTextMatch ||
        equivalentCreateMatch
      );
    });
  }, [debouncedSearch, isCreateOptionMatch, options]);

  const normalizedSearch = searchValue.trim().replace(/\s+/g, " ");
  const normalizedSearchKey = normalizedSearch.toLowerCase();
  const canCreateOption =
    !loading &&
    !!onCreateOption &&
    !!normalizedSearch &&
    (isCreateOptionAllowed?.(normalizedSearch) ?? true) &&
    !options.some(
      (option) =>
        isCreateOptionMatch?.(option, normalizedSearch) ??
        (option.label.trim().replace(/\s+/g, " ").toLowerCase() ===
          normalizedSearchKey),
    );

  const handleOpenChange = React.useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setSearchValue("");
      setDebouncedSearch("");
    }
  }, []);

  const handleSelect = React.useCallback(
    (selectedValue: string) => {
      const nextValue = selectedValue === value ? "" : selectedValue;
      onValueChange?.(nextValue);
      handleOpenChange(false);
    },
    [handleOpenChange, onValueChange, value],
  );

  const handleCreate = React.useCallback(() => {
    if (!onCreateOption || !normalizedSearch) return;
    handleOpenChange(false);
    onCreateOption(normalizedSearch);
  }, [handleOpenChange, normalizedSearch, onCreateOption]);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          disabled={disabled}
          className={cn(
            // Field-shaped trigger: match Input's neutral fill.
            "bg-muted hover:bg-muted w-full justify-between",
            className,
          )}
        >
          <span className="min-w-0 truncate text-left">
            {selectedOption?.label ?? placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        portal={!disablePortal}
        portalContainer={portalContainer}
        className={cn(
          "z-[100] w-[var(--radix-popover-trigger-width)] max-w-none p-0",
          contentClassName,
        )}
        align="start"
      >
        <Command label={searchPlaceholder} shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={searchValue}
            onValueChange={setSearchValue}
          />
          <CommandList id={listboxId}>
            {loading ? (
              <CommandEmpty>Loading...</CommandEmpty>
            ) : filteredOptions.length === 0 && !canCreateOption ? (
              <CommandEmpty>{emptyText}</CommandEmpty>
            ) : (
              <>
                {filteredOptions.length > 0 && (
                  <CommandGroup>
                    {filteredOptions.map((option) => (
                      <CommandItem
                        key={option.value}
                        value={option.value}
                        onSelect={() => handleSelect(option.value)}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            value !== option.value && "text-transparent",
                          )}
                        />
                        <div className="flex flex-col">
                          <span>{option.label}</span>
                          {option.sublabel ? (
                            <span className="text-muted-foreground text-xs">
                              {option.sublabel}
                            </span>
                          ) : null}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                {canCreateOption && (
                  <>
                    {filteredOptions.length > 0 && <CommandSeparator />}
                    <CommandGroup>
                      <CommandItem
                        value={`create-${normalizedSearch}`}
                        onSelect={handleCreate}
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        <span>
                          {createOptionLabel} “{normalizedSearch}”
                        </span>
                      </CommandItem>
                    </CommandGroup>
                  </>
                )}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
